/**
 * Compressione client-side delle foto analisi (feb 2026).
 *
 * Perché: con 5 viste per analisi, foto non compresse (2-4MB l'una in
 * base64) rischiano di superare il limite BSON di 16MB per documento
 * MongoDB quando lo storage R2 è disattivo, e appesantiscono inutilmente
 * upload e pipeline. Claude Vision ridimensiona comunque internamente le
 * immagini a ~1568px sul lato lungo: oltre quella soglia non c'è alcun
 * guadagno diagnostico.
 *
 * Strategia conservativa:
 *   - lato lungo max 1600px, JPEG qualità 0.85;
 *   - file già piccoli E dentro i limiti dimensionali → passthrough
 *     (nessuna ricompressione inutile);
 *   - qualsiasi errore di decodifica → fallback al file originale.
 */

const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.85;
const PASSTHROUGH_MAX_BYTES = 900 * 1024; // sotto ~900KB non ricomprimiamo

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Lettura file fallita"));
    reader.readAsDataURL(file);
  });
}

/**
 * Converte un File immagine in { dataUrl, base64 } ottimizzati.
 */
export async function fileToOptimizedImage(file) {
  const originalDataUrl = await readAsDataUrl(file);
  try {
    // `from-image` applica l'orientamento EXIF (foto da smartphone/camera)
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const longSide = Math.max(bitmap.width, bitmap.height);
    if (file.size <= PASSTHROUGH_MAX_BYTES && longSide <= MAX_SIDE) {
      bitmap.close?.();
      return { dataUrl: originalDataUrl, base64: originalDataUrl.split(",")[1] };
    }
    const scale = Math.min(1, MAX_SIDE / longSide);
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    return { dataUrl, base64: dataUrl.split(",")[1] };
  } catch {
    // Browser senza createImageBitmap o immagine non decodificabile:
    // meglio l'originale che bloccare l'operatore.
    return { dataUrl: originalDataUrl, base64: originalDataUrl.split(",")[1] };
  }
}
